// File: SftpManager.js
// This component handles the SFTP file management functionalities such as listing, uploading, and downloading files.
import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';
import { useAuth } from '../../../context/AuthContext';

const SftpManager = () => {
  const { apiFetch } = useAuth();
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [remotePath, setRemotePath] = useState('/');
  const [statusMsg, setStatusMsg] = useState('');
  const [loading, setLoading] = useState(false);

  // Load file list on mount
  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_ENDPOINTS.SFTP_LIST_FILES}?path=${remotePath}`);
      const data = await res.json();
      if (data.files) {
        setFiles(data.files);
        setStatusMsg('');
      } else {
        setStatusMsg(data.error || 'Failed to list files');
        setFiles([]);
      }
    } catch (err) {
      setStatusMsg('Error loading files.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile) return setStatusMsg('Please select a file to upload.');
    setStatusMsg('Uploading...');
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('remote_path', `${remotePath}/${selectedFile.name}`);

    try {
      const res = await apiFetch(API_ENDPOINTS.SFTP_UPLOAD_FILE, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      setStatusMsg(data.success ? 'Upload successful!' : `Upload failed: ${data.error}`);
      console.log('Upload response:', data);
      fetchFiles();
    } catch (err) {
      setStatusMsg('Upload error.');
    }
  };

  const handleDownload = (fileName) => {
    const url = API_ENDPOINTS.SFTP_DOWNLOAD_FILE(`${remotePath}/${fileName}`);
    window.open(url, '_blank');
  };

  return (
    <div className="container mt-4">
      <h3>SFTP File Manager</h3>

      <div className="mb-3">
        <button className="btn btn-primary mt-2" onClick={fetchFiles}>
          Refresh File List
        </button>
      </div>

      <div className="mb-3">
        <label className="form-label">Select File to Upload</label>
        <input className="form-control" type="file" onChange={handleFileChange} />
        <button className="btn btn-success mt-2" onClick={handleUpload}>
          Upload
        </button>
      </div>

      {statusMsg && <div className="alert alert-info">{statusMsg}</div>}

      <h5>Files</h5>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <ul className="list-group">
          {files.map((f) => (
            <li className="list-group-item d-flex justify-content-between" key={f.name}>
              <span>{f.name} ({f.size} bytes)</span>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => handleDownload(f.name)}
              >
                Download
              </button>
            </li>
          ))}
          {files.length === 0 && <li className="list-group-item">No files found.</li>}
        </ul>
      )}
    </div>
  );
};

export default SftpManager;